'use strict';

/**
 * Authentication utilities and helper functions
 * DOM caching, copy functionality, tooltips, and mobile behavior
 */
window.SoracomAuthUtils = (() => {

  // UI Constants
  const COPY_FEEDBACK_DURATION = 1200;

  // Security utility functions
  /**
   * Check if we're in a development environment
   * @returns {boolean} - True if in development
   */
  function isDevelopmentEnvironment() {
    const hostname = window.location.hostname;
    return hostname === 'localhost' || hostname === '127.0.0.1' || hostname.includes('localhost');
  }

  /**
   * Sanitize user input for logging to prevent log injection attacks
   * @param {any} input - Input to sanitize
   * @returns {string} - Sanitized string safe for logging
   */
  function sanitizeForLogging(input) {
    if (input === null || input === undefined) {
      return '[null]';
    }

    let sanitized = String(input);

    // Remove/replace potentially dangerous characters for log injection
    sanitized = sanitized
      .replace(/[\r\n]/g, ' ')
      .replace(/[\x00-\x1F\x7F]/g, '')
      .replace(/\x1B\[[0-9;]*[mGKF]/g, '')
      .slice(0, 500);

    return sanitized;
  }

  /**
   * Validate URL for potential security issues
   * @param {string} url - URL to validate
   * @returns {boolean} - True if URL appears safe
   */
  function validateURL(url) {
    if (!url || typeof url !== 'string') {
      return false;
    }

    // Check for suspicious schemes
    if (url.match(/^(javascript|data|vbscript|file|ftp):/i)) {
      return false;
    }

    // Check for potential XSS payloads
    if (url.includes('<script>') || url.includes('javascript:') || url.includes('vbscript:')) {
      return false;
    }

    try {
      const urlObj = new URL(url);

      // Only allow HTTP/HTTPS
      if (urlObj.protocol !== 'http:' && urlObj.protocol !== 'https:') {
        return false;
      }

      return true;
    } catch (e) {
      return false;
    }
  }

  /**
   * Validate and sanitize API endpoint URLs
   * @param {string} endpoint - API endpoint to validate
   * @returns {string|null} - Sanitized endpoint or null if invalid
   */
  function validateApiEndpoint(endpoint) {
    if (!endpoint || typeof endpoint !== 'string') {
      return null;
    }

    // Remove any potential injection attempts
    const sanitized = endpoint.replace(/[<>'"]/g, '');

    // Check if it's a valid API endpoint pattern
    if (sanitized.match(/^https:\/\/[a-zA-Z0-9.-]+\.soracom\.io(\/.*)?$/)) {
      return sanitized;
    }

    // Allow localhost for development
    if (sanitized.match(/^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?(\/.*)?$/)) {
      return sanitized;
    }

    return null;
  }

  /**
   * Enhanced error message sanitization for security
   * @param {any} error - Error object or message
   * @returns {string} - Safe error message for display
   */
  function sanitizeErrorMessage(error) {
    if (!error) {
      return 'An error occurred';
    }

    let message = String(error.message || error);

    // Remove potential sensitive information
    message = message
      .replace(/api[_-]?key[s]?[:\s=][^\s&]*/gi, 'apiKey=[REDACTED]')
      .replace(/token[s]?[:\s=][^\s&]*/gi, 'token=[REDACTED]')
      .replace(/password[s]?[:\s=][^\s&]*/gi, 'password=[REDACTED]')
      .replace(/secret[s]?[:\s=][^\s&]*/gi, 'secret=[REDACTED]')
      .replace(/authorization[:\s=][^\s&]*/gi, 'authorization=[REDACTED]')
      .replace(/Bearer\s+[^\s&]+/gi, 'Bearer [REDACTED]')
      .replace(/\b[A-Za-z0-9]{32,}\b/g, '[LONG_STRING_REDACTED]');

    return sanitizeForLogging(message);
  }



  /**
   * Production-safe console logging wrapper
   * Only logs in development environment to reduce production noise
   * @param {string} level - Log level ('error', 'warn', 'info', 'log')
   * @param {string} message - Message to log
   * @param {any} data - Optional data to log
   */
  function productionSafeLog(level, message, data = null) {
    // Always allow error and warn messages for debugging
    if (level === 'error' || level === 'warn') {
      if (data !== null) {
        console[level](message, data);
      } else {
        console[level](message);
      }
    }
    // Only allow info/log messages in development
    else if ((level === 'info' || level === 'log') && isDevelopmentEnvironment()) {
      if (data !== null) {
        console[level](message, data);
      } else {
        console[level](message);
      }
    }
  }

  /**
   * Validate timeout duration to prevent setTimeout injection
   * @param {number} duration - Duration in milliseconds
   * @returns {number} - Safe duration value
   */
  function validateTimeoutDuration(duration) {
    if (typeof duration !== 'number' || isNaN(duration) || duration < 0 || duration > 30000) {
      return COPY_FEEDBACK_DURATION;
    }
    return Math.floor(duration);
  }

  /**
   * Validate JWT token structure and basic security checks
   * @param {string} token - JWT token to validate
   * @returns {boolean} - True if JWT structure is valid
   */
  function validateJWTStructure(token) {
    if (!token || typeof token !== 'string') {
      return false;
    }

    // Basic length check - JWT tokens should be reasonable length
    if (token.length < 50 || token.length > 8192) {
      return false;
    }

    // Check for suspicious patterns that might indicate injection
    if (token.includes('<script>') || token.includes('javascript:') || token.includes('data:')) {
      return false;
    }

    const parts = token.split('.');
    if (parts.length !== 3) {
      return false;
    }

    // Check each part is valid base64url
    const base64urlPattern = /^[A-Za-z0-9_-]+$/;
    for (const part of parts) {
      if (!part || !base64urlPattern.test(part)) {
        return false;
      }
    }

    try {
      // Validate header structure
      const header = JSON.parse(atob(parts[0].replace(/-/g, '+').replace(/_/g, '/')));
      if (!header.alg || !header.typ || header.typ !== 'JWT') {
        return false;
      }

      // Validate payload structure
      const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));

      // Check for required claims and reasonable expiration
      if (payload.exp && payload.exp < Math.floor(Date.now() / 1000)) {
        productionSafeLog('warn', 'JWT token has expired');
        return false;
      }

      // Check for future issued time (clock skew tolerance of 5 minutes)
      if (payload.iat && payload.iat > Math.floor(Date.now() / 1000) + 300) {
        productionSafeLog('warn', 'JWT token issued in the future');
        return false;
      }

      return true;
    } catch (e) {
      return false;
    }
  }

  // DOM Cache for frequently accessed elements
  const DOM_CACHE = {
    coverageOptions: null,
    authPanel: null,
    coverageLabel: null,
    coverageCheckmark: null,
    authGatedContainers: null,
    copyButtons: null,

    getCoverageOptions() {
      if (!this.coverageOptions || this.coverageOptions.length === 0) {
        this.coverageOptions = document.querySelectorAll('.coverage');
      }
      return this.coverageOptions;
    },

    getAuthPanel() {
      if (!this.authPanel) {
        this.authPanel = document.getElementById('sandbox-authentication');
      }
      return this.authPanel;
    },

    getCoverageLabel() {
      if (!this.coverageLabel) {
        this.coverageLabel = document.getElementById('coverage-label');
      }
      return this.coverageLabel;
    },

    getCoverageCheckmark() {
      if (!this.coverageCheckmark) {
        this.coverageCheckmark = document.getElementById('coverage-checkmark');
      }
      return this.coverageCheckmark;
    },

    getAuthGatedContainers() {
      if (!this.authGatedContainers) {
        this.authGatedContainers = document.querySelectorAll('[data-auth-required], .auth-required-content, .protected-content');
      }
      return this.authGatedContainers;
    },

    getCopyButtons() {
      if (!this.copyButtons) {
        this.copyButtons = document.querySelectorAll('.copy-button');
      }
      return this.copyButtons;
    },

    invalidate() {
      this.coverageOptions = null;
      this.authPanel = null;
      this.coverageLabel = null;
      this.coverageCheckmark = null;
      this.authGatedContainers = null;
      this.copyButtons = null;
    }
  };

  /**
   * Handle copy button functionality with visual feedback
   * @param {HTMLElement} button - Copy button element
   */
  function handleCopyButton(button) {
    let textToCopy = '';

    if (button.dataset && button.dataset.copyTarget) {
      const targetEl = document.getElementById(button.dataset.copyTarget);
      if (targetEl) {
        textToCopy = targetEl.value || targetEl.textContent || '';
      }
    }

    if (!textToCopy) {
      const textarea = button.parentElement ? button.parentElement.querySelector('textarea') : null;
      const inputField = button.parentElement ? button.parentElement.querySelector('input[type="text"]') : null;
      const codeElement = button.parentElement ? button.parentElement.querySelector('code') : null;
      const preElement = button.parentElement ? button.parentElement.querySelector('pre') : null;

      if (textarea) {
        textToCopy = textarea.value;
      } else if (inputField) {
        textToCopy = inputField.value;
      } else if (codeElement) {
        textToCopy = codeElement.textContent;
      } else if (preElement) {
        textToCopy = preElement.textContent;
      }
    }

    if (!textToCopy && button.dataset.copyText) {
      textToCopy = button.dataset.copyText;
    }

    if (!textToCopy) {
      const parent = button.closest('.highlight, .code-block, .copy-container, .readonly-field-container');
      if (parent) {
        const codeInParent = parent.querySelector('code, pre, textarea, input[type="text"]');
        if (codeInParent) {
          textToCopy = codeInParent.textContent || codeInParent.value;
        }
      }
    }

    if (!textToCopy) {
      showCopyFeedback(button, false, 'No text to copy');
      return;
    }

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(textToCopy)
        .then(() => {
          showCopyFeedback(button, true, 'Copied!');
        })
        .catch(err => {
          console.warn('Failed to copy with modern API:', sanitizeForLogging(err?.message || err) || '[error details unavailable]');
          fallbackCopy(textToCopy, button);
        });
    } else {
      fallbackCopy(textToCopy, button);
    }
  }

  /**
   * Fallback copy method for older browsers
   * @param {string} text - Text to copy
   * @param {HTMLElement} button - Copy button element
   */
  function fallbackCopy(text, button) {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand('copy');
      showCopyFeedback(button, successful, successful ? 'Copied!' : 'Copy failed');
    } catch (err) {
      console.warn('Fallback copy failed:', sanitizeForLogging(err?.message || err) || '[error details unavailable]');
      showCopyFeedback(button, false, 'Copy failed');
    } finally {
      document.body.removeChild(textArea);
    }
  }

  /**
   * Show visual feedback for copy operations
   * @param {HTMLElement} button - Copy button element
   * @param {boolean} success - Whether copy was successful
   * @param {string} message - Feedback message
   */
  function showCopyFeedback(button, success, message) {
    const originalIcon = button.querySelector('i');
    const originalClass = originalIcon ? originalIcon.className : '';
    const originalTitle = button.title;

    // Remove any previous feedback classes
    button.classList.remove('copy-feedback', 'copy-feedback-success', 'copy-feedback-error');

    if (originalIcon) {
      originalIcon.className = success ? 'fa fa-check' : 'fa fa-times';
      originalIcon.style.color = success ? '#28a745' : '#dc3545';
    }

    button.classList.add('copy-feedback');
    button.classList.add(success ? 'copy-feedback-success' : 'copy-feedback-error');
    button.title = message;

    // Use validated timeout duration
    const safeDuration = validateTimeoutDuration(COPY_FEEDBACK_DURATION);
    setTimeout(() => {
      if (originalIcon) {
        originalIcon.className = originalClass || 'fa fa-copy';
        originalIcon.style.color = '';
      }
      button.classList.remove('copy-feedback', 'copy-feedback-success', 'copy-feedback-error');
      button.title = originalTitle;
    }, safeDuration);
  }

  /**
   * Update copy button states based on target field content
   */
  function updateCopyButtonsState() {
    const buttons = DOM_CACHE.getCopyButtons();
    buttons.forEach(btn => {
      const targetId = btn.dataset.copyTarget;
      if (!targetId) return;
      const targetEl = document.getElementById(targetId);
      if (!targetEl) return;

      const value = targetEl.value !== undefined ? targetEl.value : targetEl.textContent;
      const hasContent = value && value.trim().length > 0;

      btn.disabled = !hasContent;
    });
  }

  /**
   * Set up copy button enablement observers
   */
  function setupCopyButtonEnablement() {
    updateCopyButtonsState();

    window.addEventListener('auth-changed', updateCopyButtonsState);

    ['operatorId', 'apiKey', 'apiToken'].forEach(id => {
      const el = document.getElementById(id);
      if (el) {
        el.addEventListener('input', updateCopyButtonsState);
      }
    });
  }

  /**
   * Show tooltip with help information
   * @param {HTMLElement} element - Element to attach tooltip to
   * @param {string} text - Tooltip text
   * @param {string} mode - Tooltip mode ('sticky' or 'hover')
   */
  function showTooltip(element, text, mode = 'sticky') {
    hideTooltip();

    const tooltip = document.createElement('div');
    tooltip.className = `auth-help-tooltip ${mode}`;

    if (!element.dataset.tooltipId) {
      element.dataset.tooltipId = Math.random().toString(36).substr(2, 6);
    }
    tooltip.dataset.forIcon = element.dataset.tooltipId;
    tooltip.dataset.mode = mode;

    const tooltipContent = document.createElement('div');
    tooltipContent.className = 'tooltip-content';

    const titleEl = document.createElement('h4');
    titleEl.textContent = window.LANG?.AUTH_HELP_TOOLTIP_TITLE;

    const descEl = document.createElement('p');
    descEl.textContent = window.LANG?.AUTH_HELP_TOOLTIP_DESCRIPTION;

    const linkEl = document.createElement('a');
    linkEl.href = window.LANG?.AUTH_HELP_TOOLTIP_LINK_URL || '/docs/security/authkeys';
    linkEl.target = '_blank';
    linkEl.className = 'tooltip-link';
    linkEl.textContent = `${window.LANG?.AUTH_HELP_TOOLTIP_LINK_TEXT}`;

    tooltipContent.appendChild(titleEl);
    tooltipContent.appendChild(descEl);
    tooltipContent.appendChild(linkEl);

    tooltip.appendChild(tooltipContent);

    tooltip.addEventListener('click', e => {
      e.stopPropagation();
    });

    document.body.appendChild(tooltip);

    const rect = element.getBoundingClientRect();
    const tooltipRect = tooltip.getBoundingClientRect();

    let left = rect.left + (rect.width / 2) - (tooltipRect.width / 2);
    let top = rect.bottom + 12;

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    if (left < 10) left = 10;
    if (left + tooltipRect.width > viewportWidth - 10) {
      left = viewportWidth - tooltipRect.width - 10;
    }

    if (top + tooltipRect.height > viewportHeight - 10) {
      top = rect.top - tooltipRect.height - 10;
    }

    tooltip.style.left = `${left}px`;
    tooltip.style.top = `${top}px`;

    requestAnimationFrame(() => {
      tooltip.classList.add('show');
    });
  }

  /**
   * Hide tooltip
   */
  function hideTooltip() {
    const existingTooltip = document.querySelector('.auth-help-tooltip');
    if (existingTooltip) {
      existingTooltip.remove();
    }
  }

  /**
   * Set up tooltip system for help icons
   */
  function setupTooltips() {
    document.addEventListener('click', e => {
      const helpIcon = e.target.closest('.auth-help-icon');
      if (helpIcon) {
        const existing = document.querySelector('.auth-help-tooltip');
        if (existing) {
          hideTooltip();
          const currentTarget = existing.dataset.forIcon;
          if (currentTarget === helpIcon.dataset.tooltipId) {
            return;
          }
        }
        const ttText = helpIcon.getAttribute('data-tooltip');
        if (ttText) {
          showTooltip(helpIcon, ttText, 'sticky');
        }
        return;
      }
      if (!e.target.closest('.auth-help-tooltip')) {
        hideTooltip();
      }
    }, true);

    document.addEventListener('scroll', hideTooltip, { passive: true, capture: true });
  }

  /**
   * Initialize mobile sidebar collapse behavior
   */
  function initMobileSidebarCollapse() {
    document.addEventListener('click', (event) => {
      if (window.innerWidth >= 850) return;

      if (!document.body.classList.contains('api') || !document.getElementById('sidebar')) return;

      const sidebarContent = event.target.closest('#sidebar-content');
      if (!sidebarContent) return;

      const clickedLi = event.target.closest('li');
      if (!clickedLi) return;

      const dataUnique = clickedLi.getAttribute('data-unique');
      if (!dataUnique) return;

      if (!dataUnique.match(/\/.*?\/.*/)) return;

      setTimeout(() => {
        document.body.classList.remove('sidebar');
      }, 100);
    });
  }

  /**
   * Initialize click-outside-sidebar functionality
   */
  function initClickOutsideSidebar() {
    document.addEventListener('click', function (event) {
      if (window.innerWidth >= 850) return;

      if (!document.body.classList.contains('api') || !document.body.classList.contains('sidebar')) return;

      const sidebar = document.getElementById('sidebar');
      const sidebarToggle = document.getElementById('sidebar-toggle');

      if (!sidebar) return;

      if (sidebar.contains(event.target) || (sidebarToggle && sidebarToggle.contains(event.target))) {
        return;
      }

      document.body.classList.remove('sidebar');
    });
  }

  // Public API
  return {
    DOM_CACHE,
    handleCopyButton,
    updateCopyButtonsState,
    setupCopyButtonEnablement,
    showTooltip,
    hideTooltip,
    setupTooltips,
    initMobileSidebarCollapse,
    initClickOutsideSidebar,
    sanitizeForLogging,
    validateURL,
    validateApiEndpoint,
    sanitizeErrorMessage,
    validateTimeoutDuration,
    validateJWTStructure,
    isDevelopmentEnvironment,
    productionSafeLog
  };

})();